import { Box, Stack, type SxProps, type Theme } from "@mui/material";
import { mergeSxProps } from "@src/appV2/lib";
import { Button } from "@src/appV2/redesign/components/Button";
import { IconButton } from "@src/appV2/redesign/components/IconButton";
import { Text } from "@src/appV2/redesign/components/Text";

import { sanitizeHtml } from "../lib/sanitizeHtml";
import { type GuideButtonContent } from "../types";

export interface BannerContent {
  body: string;
  dismissible?: boolean;
  primaryButton?: GuideButtonContent;
  secondaryButton?: GuideButtonContent;
  title: string;
}

export const KNOCK_GUIDES_BANNER_VARIANTS = ["compact", "spotlight"] as const;
export type KnockGuidesBannerVariant = (typeof KNOCK_GUIDES_BANNER_VARIANTS)[number];

export interface KnockGuidesBannerViewProps {
  content: BannerContent;
  /**
   * Visual treatment.
   *
   * - `"compact"` (default) — refined Coachmark-style banner. Left-aligned `body1` title, small
   *   primary + link buttons. Use for contextual/utility messaging that sits alongside other UI.
   * - `"spotlight"` — marketing/announcement card. Center-aligned `h5` title, full-width primary
   *   + outlined secondary buttons, more breathing room. Mirrors mobile's `ShiftDiscoveryGuidesBanner`
   *   pattern. Use for prominent announcements and onboarding nudges that should stand out.
   */
  variant?: KnockGuidesBannerVariant;
  onPrimaryClick?: (button: GuideButtonContent) => void | Promise<void>;
  onSecondaryClick?: (button: GuideButtonContent) => void | Promise<void>;
  onDismiss?: () => void;
  sx?: SxProps<Theme>;
}

/**
 * Pure presentational banner that renders Knock guide content. The container component
 * (`KnockGuidesBanner`) is responsible for fetching guides via `useGuides` and forwarding callbacks.
 *
 * Two variants share the same `BannerContent` shape so the Knock dashboard schema stays a single
 * type — the consumer chooses the visual treatment per surface.
 */
export function KnockGuidesBannerView(props: KnockGuidesBannerViewProps) {
  const { content, variant = "compact", onPrimaryClick, onSecondaryClick, onDismiss, sx } = props;
  const { title, body, primaryButton, secondaryButton, dismissible } = content;

  const showDismiss = dismissible && onDismiss;
  const hasActions = Boolean(primaryButton ?? secondaryButton);
  const isSpotlight = variant === "spotlight";

  return (
    <Box
      sx={mergeSxProps(
        (theme: Theme) => ({
          position: "relative",
          display: "flex",
          flexDirection: "column",
          gap: theme.spacing(isSpotlight ? 4 : 3),
          padding: theme.spacing(isSpotlight ? 5 : 4),
          paddingRight:
            showDismiss && !isSpotlight ? theme.spacing(8) : theme.spacing(isSpotlight ? 5 : 4),
          backgroundColor: theme.palette.background?.standout,
          border: `${theme.borderWidth?.regular ?? "1px"} solid ${theme.palette.border?.base ?? theme.palette.divider}`,
          borderRadius:
            theme.borderRadius?.[isSpotlight ? "xLarge" : "large"] ?? theme.shape.borderRadius,
          boxShadow: theme.shadow?.card,
        }),
        sx
      )}
    >
      {showDismiss && (
        <IconButton
          aria-label="Dismiss banner"
          iconType="close"
          size="small"
          variant="muted"
          sx={(theme) => ({
            position: "absolute",
            top: theme.spacing(2),
            right: theme.spacing(2),
          })}
          onClick={onDismiss}
        />
      )}

      <Stack spacing={isSpotlight ? 2 : 1}>
        <Text
          bold={!isSpotlight}
          semibold={isSpotlight}
          variant={isSpotlight ? "h5" : "body1"}
          sx={(theme) => ({
            color: theme.palette.base?.strong ?? theme.palette.text.primary,
            wordBreak: "break-word",
            ...(isSpotlight && {
              textAlign: "center",
              textWrap: "balance",
            }),
          })}
        >
          {title}
        </Text>

        <Text
          variant="body2"
          sx={(theme) => ({
            color: theme.palette.base?.base ?? theme.palette.text.secondary,
            wordBreak: "break-word",
            ...(isSpotlight && {
              textAlign: "center",
              textWrap: "balance",
              paddingX: theme.spacing(4),
            }),
          })}
          dangerouslySetInnerHTML={sanitizeHtml({ potentiallyDangerousHtml: body })}
        />
      </Stack>

      {hasActions && (
        <Stack
          direction="row"
          spacing={isSpotlight ? 3 : 2}
          sx={{ marginTop: isSpotlight ? 2 : 1 }}
        >
          {isSpotlight ? (
            // Spotlight: secondary on left, primary on right, both full-width.
            // Mirrors mobile's `ShiftDiscoveryGuidesBanner`.
            <>
              {secondaryButton && (
                <Button
                  fullWidth
                  variant="outlined"
                  onClick={async () => {
                    await onSecondaryClick?.(secondaryButton);
                  }}
                >
                  {secondaryButton.text}
                </Button>
              )}
              {primaryButton && (
                <Button
                  fullWidth
                  variant="primary"
                  onClick={async () => {
                    await onPrimaryClick?.(primaryButton);
                  }}
                >
                  {primaryButton.text}
                </Button>
              )}
            </>
          ) : (
            // Compact: primary on left (small filled), secondary as a link to its right.
            <>
              {primaryButton && (
                <Button
                  size="small"
                  variant="primary"
                  onClick={async () => {
                    await onPrimaryClick?.(primaryButton);
                  }}
                >
                  {primaryButton.text}
                </Button>
              )}
              {secondaryButton && (
                <Button
                  size="small"
                  variant="link"
                  onClick={async () => {
                    await onSecondaryClick?.(secondaryButton);
                  }}
                >
                  {secondaryButton.text}
                </Button>
              )}
            </>
          )}
        </Stack>
      )}
    </Box>
  );
}
